import React, { Component, DragEvent } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { Uuid } from "@formant/core/model/Uuid";
import { Icon } from "@formant/ui/component/common/Icons/Icon";

import { appStore, commandTemplateStore, routerStore } from "../../../stores";
import { sortCommandTemplatesByDisplayOrder } from "../../../utils/commandTemplateOrder";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import * as styles from "./index.scss";

@observer
export class CommandTemplates extends Component {
    @localSettingObservable("settings-commands-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-commands-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<ICommandTemplate>[] = [
            {
                header: "",
                headerName: "Order",
                className: styles["drag-handle-control"],
                row: command => (
                    <span
                        className={styles["drag-handle-control"]}
                        draggable={!!command.id}
                        data-testid={`command-reorder-${command.name}`}
                        title="Drag to set Issue a Command order"
                        onClick={event => event.stopPropagation()}
                        onDragStart={event =>
                            this.onDragStart(event, command.id)
                        }
                        onDragOver={this.onDragOver}
                        onDrop={event => this.onDrop(event, command.id)}
                    >
                        <Icon name="icon-drag" />
                    </span>
                ),
            },
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="command" />,
                sortBy: _ => _.name,
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions commandTemplate={_} />,
            },
        ];

        const commandTemplates = sortCommandTemplatesByDisplayOrder(
            commandTemplateStore.list() || [],
        );
        const loaded = commandTemplateStore.list() !== undefined;
        const { filter, onChangeFilter, sort, onChangeSort } = this;
        const dimensions = [
            {
                dimensionKey: "parameterEnabled",
                dimensionName: "Parameter",
                getValue: getCommandParameterEnabled,
                getValueLabel: (v: boolean) => (v ? "Enabled" : "Disabled"),
            },
            ...tagDimensions(commandTemplates || [], _ => _.tags),
        ];
        const filteredCommandTemplates = applySimpleFilter(
            commandTemplates || [],
            dimensions,
            getCommandText,
            filter,
        );

        return (
            <SettingsPage
                title="Commands"
                extra={
                    <Help
                        text="A command is a directive to the device to perform a specific task. You could use the built-in commands (available by default) or create custom commands (built using Formant APIs). Drag the handle to set the order operators see in Issue a Command."
                        to="https://docs.formant.io/docs/commands"
                    />
                }
                actions={
                    <StandardButton
                        to={routeTo.createCommandTemplate()}
                        data-testid="add-new-command-button"
                    >
                        Add command
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="command"
                    loading={!loaded}
                    totalQuantity={commandTemplates.length}
                    totalLabel="Commands"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={commandTemplates}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredCommandTemplates}
                        columns={columns}
                        loading={!loaded}
                        noResultsText="No commands found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onDragStart = (
        event: DragEvent<HTMLSpanElement>,
        id: Uuid | undefined,
    ) => {
        event.stopPropagation();
        if (!id) {
            return;
        }
        event.dataTransfer.setData("text/plain", id);
        event.dataTransfer.effectAllowed = "move";
    };

    private onDragOver = (event: DragEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    private onDrop = async (
        event: DragEvent<HTMLSpanElement>,
        dropId: Uuid | undefined,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        const moveId = event.dataTransfer.getData("text/plain");
        if (!moveId || !dropId || moveId === dropId) {
            return;
        }
        try {
            await commandTemplateStore.reorder(moveId, "before", dropId);
        } catch (err) {
            console.error("Failed reordering commands", { err });
            appStore.setSnackbar("Failed reordering commands");
        }
    };

    private onEdit = async (commandTemplate: ICommandTemplate) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editCommandTemplate(
                { id: defined(commandTemplate.id) },
                { tab: "Settings" },
            ),
        );
    };
}

function getCommandText(command: ICommandTemplate): string {
    return [
        command.name,
        command.description,
        command.parameterValue || "",
    ].join(" ");
}

function getCommandParameterEnabled(command: ICommandTemplate): boolean {
    return command.parameterEnabled;
}
